import { Link, useLocation, useParams } from 'react-router-dom';
import icAngleRight from './assets/img/ic-angle-right.svg';

const Breadcrumb = ({ countryName, cityName, subPage, label, fromCountry, companyName } = {}) => {
    const location = useLocation();
    const { id, cityId: cityIdParam, companyId } = useParams();
    const actualCityId = cityIdParam || id;

    // Determine if we are on a country, company, user, city, or ticket route
    const pathname = location.pathname;

    const isUserRoute =
        pathname === '/onboarding-user-list' ||
        pathname.startsWith('/user');

    const isCountryRoute =
        pathname === '/onboarding-country-list' ||
        pathname.startsWith('/country');

    const isCityRoute =
        pathname === '/onboarding-city-list' ||
        pathname.startsWith('/city');

    const isTicketRoute =
        pathname === '/ticket-list' ||
        pathname.startsWith('/ticket');

    const isDonationsRoute =
        pathname === '/donations-list' ||
        pathname === '/donations-my' ||
        pathname.startsWith('/donations');

    const isReportingRoute = pathname.startsWith('/reporting');

    const isDiscoveryRoute =
        pathname === '/discovery/companies' ||
        pathname.startsWith('/discovery/');

    const isProfileRoute = pathname.startsWith('/profile');

    const isSupportList = pathname === '/support';
    const isSupportView = pathname.startsWith('/support/');

    const isCommunitiesList = pathname === '/communities';
    const isCommunityView = pathname.startsWith('/communities/');

    // Define breadcrumbs structure based on the route
    const breadcrumbs = isSupportList
        ? [{ label: 'Home', path: '/' }, { label: 'Support requests' }]
        : isSupportView
        ? [{ label: 'Home', path: '/' }, { label: 'Support requests', path: '/support' }, { label: label || `#${id || ''}` }]
        : isCommunitiesList
        ? [{ label: 'Home', path: '/' }, { label: 'Communities' }]
        : isCommunityView
        ? [{ label: 'Home', path: '/' }, { label: 'Communities', path: '/communities' }, { label: label || 'Community' }]
        : isProfileRoute
        ? pathname === '/profile/academics'
            ? [{ label: 'Profile', path: '/profile' }, { label: 'Academics' }]
            : pathname === '/profile/work-preferences'
            ? [{ label: 'Profile', path: '/profile' }, { label: 'Work Preferences' }]
            : [{ label: 'Profile', path: '/profile' }, { label: 'About' }]
        : isDiscoveryRoute
        ? fromCountry
            ? [
                { label: "Discovery", path: "/discovery/countries" },
                { label: "Countries", path: "/discovery/countries" },
                { label: countryName || fromCountry, path: `/discovery/countries/${fromCountry}` },
                { label: "Cities", path: `/discovery/countries/${fromCountry}/cities` },
                ...(cityName ? [{ label: cityName, ...(subPage ? { path: `/discovery/countries/${fromCountry}/cities/${actualCityId}` } : {}) }] : []),
                ...(subPage ? [{ label: subPage, ...(companyName || label ? { path: `/discovery/countries/${fromCountry}/cities/${actualCityId}/${subPage.toLowerCase()}` } : {}) }] : []),
                ...(companyName ? [{ label: companyName, path: `/discovery/countries/${fromCountry}/cities/${actualCityId}/${subPage?.toLowerCase()}/${companyId}` }] : []),
                ...(label ? [{ label }] : []),
            ]
        : pathname.startsWith('/discovery/countries')
            ? [
                { label: "Discovery", path: "/discovery/countries" },
                { label: "Countries", ...(countryName ? { path: "/discovery/countries" } : {}) },
                ...(countryName ? [{ label: countryName, path: `/discovery/countries/${id}` }] : []),
                ...(subPage && cityName
                    ? [{ label: subPage, path: `/discovery/countries/${id}/cities` }]
                    : subPage
                    ? [{ label: subPage, ...(companyName || label ? { path: `/discovery/countries/${id}/${subPage.toLowerCase()}` } : {}) }]
                    : []),
                ...(cityName ? [{ label: cityName }] : []),
                ...(companyName ? [{ label: companyName, path: `/discovery/countries/${id}/${subPage?.toLowerCase()}/${companyId}` }] : []),
                ...(label ? [{ label }] : []),
            ]
            : pathname.startsWith('/discovery/cities')
            ? [
                { label: "Discovery", path: "/discovery/countries" },
                { label: "Cities", ...(cityName ? { path: "/discovery/cities" } : {}) },
                ...(cityName ? [{ label: cityName, ...(subPage ? { path: `/discovery/cities/${actualCityId}` } : {}) }] : []),
                ...(subPage ? [{ label: subPage, ...(companyName || label ? { path: `/discovery/cities/${actualCityId}/${subPage.toLowerCase()}` } : {}) }] : []),
                ...(companyName ? [{ label: companyName, path: `/discovery/cities/${actualCityId}/${subPage?.toLowerCase()}/${companyId}` }] : []),
                ...(label ? [{ label }] : []),
            ]
            : pathname.startsWith('/discovery/jobs')
            ? [
                { label: "Discovery", path: "/discovery/countries" },
                { label: "Jobs" }
            ]
            : [
                { label: "Discovery", path: "/discovery/countries" },
                { label: "Companies", ...(companyName || label ? { path: "/discovery/companies" } : {}) },
                ...(companyName ? [{ label: companyName, ...(label ? { path: `/discovery/companies/${id}` } : {}) }] : []),
                ...(label ? [{ label }] : []),
            ]
        : isCountryRoute
        ? [{ label: "Onboarding Countries", path: "/onboarding-country-list" }]
        : isUserRoute
        ? [{ label: "Onboarding Users", path: "/onboarding-user-list" }]
        : isCityRoute
        ? [{ label: "Onboarding Cities", path: "/onboarding-city-list" }]
        : isTicketRoute
        ? [{ label: "Tickets", path: "/ticket-list" }]
        : isDonationsRoute
        ? [{ label: "Donations", path: "/donations-my" }]
        : isReportingRoute
        ? [
            { label: "Reporting", path: "/reporting/jobs" },
            ...(pathname === '/reporting/jobs' ? [{ label: "Jobs" }] : []),
            ...(pathname === '/reporting/general' ? [{ label: "General" }] : []),
            ...(pathname === '/reporting/users' ? [{ label: "Users" }] : []),
            ...(pathname === '/reporting/revenue' ? [{ label: "Revenue" }] : []),
        ]
        : [{ label: "Onboarding Companies", path: "/onboarding-company-list" }];
        

    if (id) {
        if (pathname.startsWith('/company-review/')) {
            breadcrumbs.push({ label: "Company Review" });
        }
        if (pathname.startsWith('/company-edit/')) {
            breadcrumbs.push({ label: "Company Edit" });
        }
        if (/^\/country-edit/.test(pathname)) {
            breadcrumbs.push({ label: "Country Edit" });
        }
        if (pathname.startsWith('/user-edit/')) {
            breadcrumbs.push({ label: "User Edit" });
        }
        if (pathname.startsWith('/city-edit/')) {
            breadcrumbs.push({ label: cityName || 'City Edit' });
        }
        if (pathname.match(/\/city-edit-companies\/[^/]+\/company\//)) {
            if (cityName) breadcrumbs.push({ label: cityName, path: `/city-edit/${cityIdParam}` });
            breadcrumbs.push({ label: 'Companies', path: `/city-edit-companies/${cityIdParam}` });
            if (companyName) breadcrumbs.push({ label: companyName });
        } else if (pathname.startsWith('/city-edit-companies/')) {
            if (cityName) breadcrumbs.push({ label: cityName, path: `/city-edit/${id}` });
            breadcrumbs.push({ label: 'Companies' });
        }
        if (pathname.match(/\/city-edit-jobs\/[^/]+\/company\//)) {
            if (cityName) breadcrumbs.push({ label: cityName, path: `/city-edit/${cityIdParam}` });
            breadcrumbs.push({ label: 'Jobs', path: `/city-edit-jobs/${cityIdParam}` });
            if (companyName) breadcrumbs.push({ label: companyName });
        } else if (pathname.startsWith('/city-edit-jobs/')) {
            if (cityName) breadcrumbs.push({ label: cityName, path: `/city-edit/${id}` });
            breadcrumbs.push({ label: 'Jobs' });
        }
        if (pathname.startsWith('/city-edit-salary/')) {
            if (cityName) breadcrumbs.push({ label: cityName, path: `/city-edit/${id}` });
            breadcrumbs.push({ label: 'Average salaries' });
        }
        if (pathname.startsWith('/city-edit-insights/')) {
            if (cityName) breadcrumbs.push({ label: cityName, path: `/city-edit/${id}` });
            breadcrumbs.push({ label: 'City insights' });
        }
        if (pathname.startsWith('/city-edit-universities/')) {
            if (cityName) breadcrumbs.push({ label: cityName, path: `/city-edit/${id}` });
            breadcrumbs.push({ label: 'Universities' });
        }
        // Ticket edit: Tickets / Ticket / Ticket Edit
        if (pathname.startsWith('/ticket-edit/')) {
            breadcrumbs.push({ label: "Ticket Edit" });
        }
    }

    // ✅ Add this outside of the id block, since /user-add likely does not have an :id param
    if (pathname === '/user-add') {
        breadcrumbs.push({ label: "User Add" });
    }

    if (pathname === '/company-add') {
        breadcrumbs.push({ label: "Company Add" });
    }

    if (pathname === '/city-add') {
        breadcrumbs.push({ label: "City Add" });
    }

    if (pathname === '/country-add') {
        breadcrumbs.push({ label: "Country Add" });
    }

    // Ticket add: Tickets / Ticket / Ticket add
    if (pathname === '/ticket-add') {
        breadcrumbs.push({ label: "Ticket add" });
    }

    return (
        <div className="breadcrumb">
            {breadcrumbs.map((breadcrumb, index) => (
                <span key={index}>
                    {breadcrumb.path ? (
                        <Link to={breadcrumb.path} className="breadcrumb-link">
                            {breadcrumb.label}
                        </Link>
                    ) : (
                        <span className="breadcrumb-current">{breadcrumb.label}</span>
                    )}
                    {index < breadcrumbs.length - 1 && (
                        <span className="breadcrumb-separator px-2">
                            <img src={icAngleRight} alt=">" />
                        </span>
                    )}
                </span>
            ))}
        </div>
    );
};

export default Breadcrumb;